import React, { useState } from 'react';
import { X, Building2, AlertCircle, CheckCircle } from 'lucide-react';
import { clientsService } from '../../services';

interface AddClientModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess?: (client: { id: string; name: string }) => void;
}

const AddClientModal: React.FC<AddClientModalProps> = ({ isOpen, onClose, onSuccess }) => {
  const [name, setName] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);

  if (!isOpen) return null;

  const handleSubmit = async () => {
    if (!name.trim()) return;
    setIsSubmitting(true);
    setError(null);
    try {
      const client = await clientsService.createClient({ name: name.trim() });
      setSuccessMessage(`Client "${client.name}" created successfully!`);
      onSuccess?.(client);
      setTimeout(() => {
        setName('');
        setSuccessMessage(null);
        onClose();
      }, 1500);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to create client');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="modal-overlay">
      <div className="modal-content add-facility-modal">
        <div className="modal-header">
          <h2>
            <Building2 size={24} />
            Add New Client
          </h2>
          <button className="close-button" onClick={onClose}>
            <X size={24} />
          </button>
        </div>
        <div className="modal-body">
          {error && (
            <div className="error-alert">
              <AlertCircle size={20} />
              <span>{error}</span>
            </div>
          )}
          {successMessage && (
            <div className="success-alert">
              <CheckCircle size={20} />
              <span>{successMessage}</span>
            </div>
          )}
          <div className="form-step">
            <div className="form-group">
              <label className="form-label">Client Name *</label>
              <input
                type="text"
                className="form-input"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="e.g., City Water Agency"
              />
            </div>
          </div>
        </div>
        <div className="modal-footer">
          <div className="footer-actions">
            <button type="button" className="btn btn-ghost" onClick={onClose}>
              Cancel
            </button>
            <button
              type="button"
              className="btn btn-primary"
              onClick={handleSubmit}
              disabled={isSubmitting || !name.trim()}
            >
              {isSubmitting ? 'Creating Client...' : 'Create Client'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AddClientModal;
